import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';

const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };

const useNotificationsModalStore = defineStore('notificationsModal', () => {
  const show = ref(false);
  const focusStationLabel = ref(null);
  const mutePreviewAlerting = ref(false);
  const mutedMessageIds = ref(new Set());
  const activeMessages = ref([]);
  const inactiveMessages = ref([]);
  const newMessages = computed(() => activeMessages.value.filter((m) => !mutedMessageIds.value.has(m.id)));

  const mutedMessages = computed(() => activeMessages.value.filter((m) => mutedMessageIds.value.has(m.id)));

  const highestSeverity = computed(() => {
    const msgs = newMessages.value;
    if (msgs.some((m) => m.level === 'error')) return 'error';
    if (msgs.some((m) => m.level === 'warning')) return 'warning';
    if (msgs.length) return 'info';
    return null;
  });

  const topNewMessage = computed(() => {
    if (!newMessages.value.length) return null;
    return [...newMessages.value].sort((a, b) => {
      const sev = (SEVERITY_ORDER[a.level] ?? 3) - (SEVERITY_ORDER[b.level] ?? 3);
      if (sev !== 0) return sev;
      return new Date(b.created_at) - new Date(a.created_at);
    })[0];
  });

  const mutedHighestSeverity = computed(() => {
    const msgs = mutedMessages.value;
    if (msgs.some((m) => m.level === 'error')) return 'error';
    if (msgs.some((m) => m.level === 'warning')) return 'warning';
    if (msgs.length) return 'info';
    return null;
  });

  const openModal = () => {
    focusStationLabel.value = null;
    show.value = true;
  };

  const openModalForStation = (label) => {
    focusStationLabel.value = label;
    show.value = true;
  };

  const closeModal = () => {
    show.value = false;
  };

  const toggleMutePreviewAlerting = () => {
    mutePreviewAlerting.value = !mutePreviewAlerting.value;
  };

  const muteMessage = (id) => {
    mutedMessageIds.value = new Set([...mutedMessageIds.value, id]);
  };

  const unmuteMessage = (id) => {
    const next = new Set(mutedMessageIds.value);
    next.delete(id);
    mutedMessageIds.value = next;
  };

  const muteAll = () => {
    mutedMessageIds.value = new Set(activeMessages.value.map((m) => m.id));
  };

  const clearInactive = () => {
    inactiveMessages.value = [];
  };

  const _setActiveMessages = (msgs) => {
    activeMessages.value = msgs;
  };

  const _addInactiveMessage = (msg) => {
    if (!inactiveMessages.value.some((m) => m.id === msg.id)) {
      inactiveMessages.value = [msg, ...inactiveMessages.value];
    }
  };

  const _removeFromInactive = (ids) => {
    if (!ids.length) return;
    const idSet = new Set(ids);
    inactiveMessages.value = inactiveMessages.value.filter((m) => !idSet.has(m.id));
  };

  const _onMessageDeparted = (id, msg) => {
    _addInactiveMessage(msg);
  };

  return {
    show: readonly(show),
    focusStationLabel: readonly(focusStationLabel),
    mutePreviewAlerting: readonly(mutePreviewAlerting),
    mutedMessageIds: readonly(mutedMessageIds),
    activeMessages: readonly(activeMessages),
    inactiveMessages: readonly(inactiveMessages),
    newMessages,
    mutedMessages,
    topNewMessage,
    highestSeverity,
    mutedHighestSeverity,

    openModal,
    openModalForStation,
    closeModal,
    toggleMutePreviewAlerting,
    muteMessage,
    unmuteMessage,
    muteAll,
    clearInactive,

    _setActiveMessages,
    _addInactiveMessage,
    _removeFromInactive,
    _onMessageDeparted,
  };
});

export default useNotificationsModalStore;
